import { Component, input, output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FeatureFunction } from '../../services/feature-function.service';

@Component({
  selector: 'app-feature-detail-modal',
  imports: [CommonModule],
  template: `
    <div class="modal-overlay" (click)="onClose()">
      <div class="modal-content" (click)="$event.stopPropagation()">
        <button class="close-btn" (click)="onClose()" aria-label="Fenster schließen">
          <span>×</span>
        </button>

        <div class="modal-header">
          <div class="feature-icon-large">{{ feature().icon }}</div>
          <h2>{{ feature().title }}</h2>
        </div>

        <div class="modal-body">
          @if (feature().imageData && feature().imageContentType) {
            <div class="feature-image-container">
              <img
                [src]="'data:' + feature().imageContentType + ';base64,' + feature().imageData"
                [alt]="feature().title"
                class="feature-image"
              />
            </div>
          }

          <div class="feature-description">
            {{ feature().detailedDescription }}
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" (click)="onClose()">
            Zurück
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.6);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2000;
      padding: 1rem;
      animation: fadeIn 0.2s ease-out;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .modal-content {
      background: white;
      border-radius: 16px;
      max-width: 600px;
      width: 100%;
      max-height: 90vh;
      overflow-y: auto;
      position: relative;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      animation: slideUp 0.3s ease-out;
    }

    @keyframes slideUp {
      from {
        transform: translateY(30px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .close-btn {
      position: absolute;
      top: 1rem;
      right: 1rem;
      background: transparent;
      border: none;
      font-size: 2rem;
      color: white;
      cursor: pointer;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s;
      z-index: 10;
    }

    .close-btn:hover {
      background-color: rgba(255, 255, 255, 0.2);
      color: white;
    }

    .modal-header {
      padding: 2rem 2rem 1.25rem;
      text-align: center;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-radius: 16px 16px 0 0;
    }

    .feature-icon-large {
      font-size: 48px;
      margin-bottom: 16px;
    }

    .modal-header h2 {
      margin: 0;
      font-size: 1.75rem;
      font-weight: 700;
    }

    .modal-body {
      padding: 1.5rem 2rem;
    }

    .feature-image-container {
      margin-bottom: 24px;
      text-align: center;
    }

    .feature-image {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .feature-description {
      font-size: 15px;
      line-height: 1.6;
      color: #555;
      white-space: pre-line;
      word-wrap: break-word;
    }

    .modal-footer {
      padding: 1.25rem 2rem;
      border-top: 1px solid #e2e8f0;
      display: flex;
      gap: 1rem;
      justify-content: flex-end;
    }

    .btn {
      padding: 10px 24px;
      font-size: 0.95rem;
      font-weight: 600;
      border-radius: 8px;
      border: none;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .btn-primary {
      background-color: #f59e0b;
      color: white;
    }

    .btn-primary:hover {
      background-color: #d97706;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
    }

    .btn-secondary {
      background-color: white;
      color: #667eea;
      border: 2px solid #667eea;
    }

    .btn-secondary:hover {
      background-color: #f1f5f9;
    }

    @media (max-width: 768px) {
      .modal-content {
        max-width: 100%;
        margin: 0;
        border-radius: 16px 16px 0 0;
      }

      .modal-header {
        padding: 1.5rem 1rem 1rem;
      }

      .modal-header h2 {
        font-size: 1.5rem;
      }

      .feature-icon-large {
        font-size: 40px;
      }

      .modal-footer {
        flex-direction: column;
      }

      .btn {
        width: 100%;
      }

      .feature-description {
        font-size: 14px;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureDetailModalComponent {
  feature = input.required<FeatureFunction>();
  close = output<void>();

  onClose() {
    this.close.emit();
  }
}
